import React from 'react';

import { Box } from '@mui/material';

import clsx from 'clsx';

import AppAddPillButton from '../../../../components/designe/buttons/app-add-pill-button/AppAddPillButton';
import AppAiPillButton from '../../../../components/designe/buttons/app-ai-pill-button/AppAiPillButton';
import AppPlayPillButton from '../../../../components/designe/buttons/app-play-pill-button/AppPlayPillButton';
import NoResults from '../../../../components/reusable/no-results';
import Search from '../../../../components/reusable/search/Search';
import WithListObservers from '../../../../hoc/with-list-observers';
import { TrainingStatus } from '../../../../shared/constants';
import { ITraining } from '../../../../types/interfaces/training';
import TrainingItemSkeleton from '../training-item-skeleton/TrainingItemSkeleton';
import { useStyles } from './TrainingList.styles';
import TrainingListItem from './training-list-item/TrainingListItem';
import { TrainingListKind } from './training-list-item/training-list-subtitle';

const SKELETON_COUNT = 4;

interface ITrainingListProps {
  trainings: ITraining[] | null;
  searchValue: string;
  onSearchChange: (value: string) => void;
  tabsHidden?: boolean;
  showAvatar?: boolean;
  isLoading?: boolean;
  addNewTrainingClicked?: () => void;
  generateTrainingClicked?: () => void;
  startClicked?: () => void;
  startPlayLoading?: boolean;
  listFooter?: React.ReactNode;
  onTrainingDeleted?: () => Promise<void> | void;
  onStartExistingTrainingRestricted?: () => void;
  sharedNavigation?: boolean;
  listKind?: TrainingListKind;
  emptyStateBanner?: React.ReactNode;
}

const TrainingList = (props: ITrainingListProps) => {
  const {
    trainings,
    searchValue,
    onSearchChange,
    tabsHidden,
    addNewTrainingClicked,
    generateTrainingClicked,
    showAvatar = false,
    startClicked,
    isLoading = false,
    startPlayLoading = false,
    listFooter,
    onTrainingDeleted,
    onStartExistingTrainingRestricted,
    sharedNavigation = false,
    listKind = 'my',
    emptyStateBanner,
  } = props;
  const { classes } = useStyles({ tabsHidden });
  const showNoResult =
    !isLoading && trainings !== null && trainings.length === 0;

  const showListWithFooter =
    !isLoading && trainings !== null && (trainings.length > 0 || listFooter);

  const showLazyListArea =
    listFooter &&
    (trainings === null || showListWithFooter) &&
    !(showNoResult && listFooter);

  return (
    <div className={classes.trainingListWrapper}>
      <Box
        className={classes.trainingItemListHeader}
        display="flex"
        alignItems="flex-end"
        gap={1}>
        <Search
          fullWidth
          value={searchValue}
          onChange={onSearchChange}
          className={classes.trainingListSearch}
        />
        {generateTrainingClicked && (
          <AppAiPillButton
            data-onboarding="trainingPage.action.generateAi"
            colorVariant="secondary"
            onClick={() => generateTrainingClicked()}
          />
        )}
        {startClicked && (
          <AppPlayPillButton
            data-onboarding="trainingPage.action.startNow"
            colorVariant="secondary"
            loading={startPlayLoading}
            onClick={() => startClicked()}
          />
        )}
        {addNewTrainingClicked && (
          <AppAddPillButton
            data-onboarding="trainingPage.action.createFuture"
            colorVariant="primary"
            onClick={() => addNewTrainingClicked()}
          />
        )}
      </Box>

      {showNoResult && emptyStateBanner && <Box mt={1}>{emptyStateBanner}</Box>}
      {showNoResult && <NoResults />}

      {showNoResult && listFooter && <Box>{listFooter}</Box>}

      {!listFooter && isLoading && (
        <WithListObservers
          wrapperClassName={clsx(
            classes.trainingItemListBody,
            classes.trainingItemListBodyWithButtons,
          )}>
          <Box display="flex" flexDirection="column">
            {Array.from({ length: SKELETON_COUNT }, (_, i) => (
              <TrainingItemSkeleton key={i} />
            ))}
          </Box>
        </WithListObservers>
      )}

      {showLazyListArea && (
        <WithListObservers
          wrapperClassName={clsx(
            classes.trainingItemListBody,
            classes.trainingItemListBodyWithButtons,
          )}>
          {isLoading ? (
            <Box display="flex" flexDirection="column">
              {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                <TrainingItemSkeleton key={i} />
              ))}
            </Box>
          ) : (
            trainings?.map((training) => (
              <TrainingListItem
                showAvatar={showAvatar}
                training={training}
                key={training.id}
                onTrainingDeleted={onTrainingDeleted}
                showStartButton={training.status === TrainingStatus.notStarted}
                onStartRestricted={onStartExistingTrainingRestricted}
                sharedNavigation={sharedNavigation}
                listKind={listKind}
              />
            ))
          )}
          {listFooter}
        </WithListObservers>
      )}

      {!listFooter && showListWithFooter && (
        <WithListObservers
          wrapperClassName={clsx(
            classes.trainingItemListBody,
            classes.trainingItemListBodyWithButtons,
          )}>
          {trainings!.map((training) => (
            <TrainingListItem
              showAvatar={showAvatar}
              training={training}
              key={training.id}
              onTrainingDeleted={onTrainingDeleted}
              showStartButton={training.status === TrainingStatus.notStarted}
              onStartRestricted={onStartExistingTrainingRestricted}
              sharedNavigation={sharedNavigation}
              listKind={listKind}
            />
          ))}
        </WithListObservers>
      )}
    </div>
  );
};

export default TrainingList;
